import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { Subscription } from 'rxjs';
import { SecuredDynamicLoadingService } from './core/services/secured-dynamic-loading.service';
import { UserSessionService } from './core/services/user-session.service';
import { DomSanitizer } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';
import { EventMessage, EventType } from '@azure/msal-browser';
import { devLog } from './core/utils/generic-utils';
import { customCompanyHomeRedirects } from './core/constants/constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private routerSubscription: Subscription | undefined;

  /** The url of the navigation start event */
  private navigationStartUrl: string | null = null;

  /** auth service and broadcast services need to be injected HERE... Otherwise other modules won't be able to use them */
  constructor(
    private router: Router,
    private dynamicLoadingService: SecuredDynamicLoadingService,
    private msalBroadcastService: MsalBroadcastService,
    private userSessionService: UserSessionService,
    private matIconRegistry: MatIconRegistry,
    private domSanitizer: DomSanitizer,
  ) {
    // Add Custom icons to MatIconRegistry to be used in mat-icon
    this.registerCustomIcons();
  }

  private registerCustomIcons(): void {
    const iconMap = {
      'icon_change-management': 'assets/svg/icon_change-management.svg',
      'icon_warranty-schedule': 'assets/images/icons/icon-warranty-schedule.svg',
      'icon_warranty-verified_user': 'assets/images/icons/icon-warranty-verified_user.svg',
      'icon_warranty-devices': 'assets/images/icons/icon-warranty-devices.svg',
      'icon_warranty-gpp_bad': 'assets/images/icons/icon-warranty-gpp_bad.svg',
      'icon_sps-addon': '/assets/images/icons/icon_sps-addon.svg',
    };

    Object.entries(iconMap).forEach(([name, path]) => {
      this.matIconRegistry.addSvgIcon(name, this.domSanitizer.bypassSecurityTrustResourceUrl(path));
    });
  }

  // meanwhile we don't have autosave
  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent) {
    this.userSessionService.validateInactivityTime();
  }

  // meanwhile we don't have autosave
  @HostListener('document:keypress', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    this.userSessionService.validateInactivityTime();
  }

  ngOnInit() {
    if (this.userSessionService.getActiveAccount() != null) {
      this.userSessionService.validateInactivityTime();
    }
    // Handle the loading of dynamic modules
    this.routerSubscription = this.router.events.subscribe((routerEvent) => {
      // devLog(routerEvent)
      if (routerEvent instanceof NavigationStart) {
        this.navigationStartUrl = routerEvent.url;
      } else if (routerEvent instanceof RouteConfigLoadStart && this.navigationStartUrl !== null) {
        const loadingMessage = `Loading ${this.routeModule(this.navigationStartUrl)}`;
        this.dynamicLoadingService.notifyModuleIsLoading(loadingMessage);
      } else if (routerEvent instanceof RouteConfigLoadEnd) {
        this.dynamicLoadingService.notifyModuleFinishedOrFailedLoading();
        this.navigationStartUrl = null;
      }
    });

    this.msalBroadcastService.msalSubject$.subscribe((subjectResponse: EventMessage) => {
      devLog({
        function: 'msalSubject$',
        data: Object.assign({}, subjectResponse),
        event: subjectResponse.eventType,
      });
      if (
        subjectResponse.eventType === EventType.LOGOUT_END ||
        subjectResponse.eventType === EventType.LOGOUT_SUCCESS
      ) {
        sessionStorage.clear();
      } else if (subjectResponse.eventType === EventType.LOGIN_SUCCESS) {
        devLog({
          function: 'msalSubject$',
          data: Object.assign({}, subjectResponse),
          event: subjectResponse.eventType,
        });

        const sessionIat = localStorage.getItem('sessionIat');
        if (sessionIat && this.userSessionService.sessionIsExpired(sessionIat)) {
          this.userSessionService.logout();
          return;
        }

        this.userSessionService.getActiveAccount();
        const payload = subjectResponse.payload as any;

        localStorage.setItem('sessionAud', payload.idTokenClaims?.aud);
        localStorage.setItem('sessionIat', payload.idTokenClaims?.iat.toString());

        // devLog('login success called!')
        if (this.userSessionService.isUserAuthenticated) {
          this.userSessionService.resetTimeout();
          // Log out the user and redirect to login with company param if available
          const sessionCompanyName = localStorage.getItem('sessionCompanyName');
          if (sessionCompanyName && customCompanyHomeRedirects[sessionCompanyName]) {
            window.location.href = customCompanyHomeRedirects[sessionCompanyName];
          } else {
            if (window.location.href.includes('/login')) {
              window.location.href = '/secured/home'; // NOTE ROUTER IS NOT WORKING HERE! Bug?!
            }
          }
        }
      } else if (
        subjectResponse.eventType === EventType.LOGIN_FAILURE &&
        subjectResponse.error.message.includes('forgotten their password')
      ) {
        this.userSessionService.passwordReset();
      }
    });
  }

  ngOnDestroy() {
    this.routerSubscription?.unsubscribe();
  }

  /** Returns the module belonging to the given route, else the route is returned */
  private routeModule(route: string): string {
    if (route.includes(`secured/support`)) {
      return `LifecycleRepair`;
    } else if (route.includes(`secured/orders`)) {
      return `Order Tracking`;
    } else if (route.includes(`secured/home`)) {
      return `Home`;
    } else if (route.includes(`secured/user-permissions`)) {
      return `User Permissions`;
    }

    return route;
  }
}
